import { DiamondButton, DiamondButtonFontSize, DiamondButtonShape } from "./DiamondButton.js";

export interface ProgressButtonOptions {
    container: HTMLElement;
    label?: string;
    visible?: boolean;
    className?: string;
    shape?: DiamondButtonShape,
    fontSize?: DiamondButtonFontSize | "auto";
    onClick?: (btn: ProgressButton, e: Event) => void;
}

export class ProgressButton {

    public readonly onVisibilityChange = e5.Signal.create<[boolean]>();
    public readonly onClick = e5.Signal.create<[ProgressButton, Event]>();
    public readonly onFilled = e5.Signal.create<[ProgressButton]>();

    private _outline: DiamondButton;
    private _fill: DiamondButton;
    private _element: HTMLElement;
    private _progress: number = 0;
    private _fillElement: HTMLElement;

    constructor(options: ProgressButtonOptions) {

        options = e5.object.merge({
            label: "",
            className: "",
            visible: true,
            shape: "dia-small",
            fontSize: "auto",
        }, options);


        this._element = document.createElement("div");
        this._element.className = "progress-button";

        this._outline = new DiamondButton({
            container: this._element,
            shape: options.shape,
            filled: false,
            className: "progress-button__outline",
            fontSize: options.fontSize,
            visible: true
        });

        this._fill = new DiamondButton({
            container: this._element,
            shape: options.shape,
            filled: true,
            className: "progress-button__fill",
            fontSize: options.fontSize,
            visible: false
        });

        this._element.addEventListener("click",
            (e) => {
                this.onClick.dispatch(this, e);
            }
        );

        if (typeof options.onClick === "function") {
            this.onClick.add(options.onClick);
        }

        this._fillElement = this._element.querySelector(".progress-button__fill");

        this.label = options.label;
        this.visible = options.visible;

        if (options.container) {
            options.container.appendChild(this._element);
        }
    }

    public get shape(): DiamondButtonShape {
        return this._outline.shape;
    }

    public set shape(value: DiamondButtonShape) {
        this._outline.shape = value;
        this._fill.shape = value;
    }

    public get fontSize(): DiamondButtonFontSize {
        return this._outline.fontSize;
    }

    public set fontSize(value: DiamondButtonFontSize) {
        this._outline.fontSize = value;
        this._fill.fontSize = value;
    }

    public get visible(): boolean {
        return !this._element.classList.contains("progress-button--hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this._element.classList.toggle("progress-button--hidden", !value);
        this.onVisibilityChange.dispatch(value);
    }

    public get label(): string {
        return this._outline.label;
    }

    public set label(value: string) {
        this._outline.label = value;
        this._fill.label = value;
    }

    public get progress(): number {
        return this._progress;
    }

    public set progress(value: number) {
        value = e5.Calc.clamp(value, 0, 1);
        if (this._progress == value) {
            return;
        }
        this._progress = value;
        this._fillElement.style.clipPath = `inset(0 ${((1 - value) * 100).toFixed(8)}% 0 0)`;
        this._fill.visible = value > 0;
    }

    public stop(): void {
        gsap.killTweensOf(this);
        this.progress = 0;
    }

    public play(duration: number = 3): void {
        this.stop();
        gsap.to(this, {
            duration: duration,
            progress: 1,
            ease: "linear",
            onComplete: () => {
                this.onFilled.dispatch(this);
            }
        });
    }
}